import { useCallback, useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { RefreshCw, Smartphone } from 'lucide-react'
import Modal from '@/components/common/Modal'
import { createHandoffCode } from '@/api/autoagent'
import { APP_BASE } from '@/config/basePath'

// "Continue on phone": a QR code the twasta Build Android app scans to log in
// as this user (no password on the phone) and open this project's build on
// the same live session. The code is single-use and expires in five minutes.

const SERVER_KEY = 'twasta.handoff.server'

function defaultServer(): string {
  try {
    const saved = localStorage.getItem(SERVER_KEY)
    if (saved) return saved
  } catch { /* storage blocked — fall through to the page origin */ }
  return `${location.origin}${APP_BASE}`
}

function isLoopback(server: string): boolean {
  try {
    const host = new URL(server).hostname
    return host === 'localhost' || host === '127.0.0.1' || host === '[::1]'
  } catch {
    return false
  }
}

export function buildHandoffUrl(server: string, code: string, project: string, planId?: string | null): string {
  const params = new URLSearchParams({ server: server.replace(/\/+$/, ''), code, project })
  if (planId) params.set('plan', planId)
  return `twasta-build://handoff?${params.toString()}`
}

interface Props {
  open: boolean
  onClose: () => void
  project: string
  planId?: string | null
}

export default function ContinueOnPhoneModal({ open, onClose, project, planId }: Props) {
  const [server, setServer] = useState<string>(defaultServer)
  const [qr, setQr] = useState<string | null>(null)
  const [expiresAt, setExpiresAt] = useState<number>(0)
  const [now, setNow] = useState<number>(() => Date.now())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const issue = useCallback(async () => {
    setLoading(true)
    setError(null)
    setQr(null)
    try {
      const target = server.trim()
      try {
        // Validate early: a typo here produces a QR the phone can never use.
        new URL(target)
      } catch {
        throw new Error('Enter the full server address, e.g. http://192.168.1.20:5173')
      }
      const h = await createHandoffCode(project, planId)
      const url = buildHandoffUrl(target, h.code, project, planId)
      const dataUrl = await QRCode.toDataURL(url, { errorCorrectionLevel: 'M', margin: 1, width: 240 })
      setQr(dataUrl)
      setExpiresAt(Date.now() + h.expires_in * 1000)
      try { localStorage.setItem(SERVER_KEY, target) } catch { /* convenience only */ }
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || (err as Error)?.message || 'Could not create a hand-off code.')
    } finally {
      setLoading(false)
    }
  }, [server, project, planId])

  // Issue a code each time the dialog opens.
  useEffect(() => {
    if (open) void issue()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Countdown so an expired QR is obviously expired.
  useEffect(() => {
    if (!open || !qr) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [open, qr])

  const secondsLeft = Math.max(0, Math.round((expiresAt - now) / 1000))
  const expired = !!qr && secondsLeft === 0

  return (
    <Modal open={open} onClose={onClose} title="Continue on your phone">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', fontSize: '0.9375rem' }}>
        <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
          Open the <strong>twasta Build</strong> app, tap <em>Scan from browser</em>, and point it at this
          code. You&apos;ll be signed in and dropped into this build — both screens stay live.
        </p>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontWeight: 600 }}>Server address the phone should use</span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              value={server}
              onChange={(e) => setServer(e.target.value)}
              spellCheck={false}
              style={{
                flex: 1, minWidth: 0, padding: '0.375rem 0.5rem', borderRadius: 6,
                border: '1px solid var(--color-border)', background: 'var(--color-bg)',
                color: 'var(--color-text)', fontFamily: 'var(--font-mono, monospace)', fontSize: '0.875rem',
              }}
            />
            <button
              type="button"
              onClick={() => void issue()}
              disabled={loading}
              title="Make a new code"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.375rem 0.625rem',
                borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
                color: 'var(--color-text)', cursor: loading ? 'default' : 'pointer',
              }}
            >
              <RefreshCw size={14} /> New code
            </button>
          </div>
          {isLoopback(server) && (
            <span style={{ color: 'var(--color-warning, #b45309)', fontSize: '0.875rem' }}>
              A phone can&apos;t reach <code>localhost</code>. Use this computer&apos;s network address
              (for example <code>http://192.168.1.20:5173</code>). The Android emulator reaches this
              computer as <code>10.0.2.2</code> — the app rewrites localhost for you there.
            </span>
          )}
        </label>

        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem',
          padding: '0.75rem', borderRadius: 8, background: 'var(--color-bg-secondary)',
        }}>
          {loading && <span style={{ color: 'var(--color-text-secondary)' }}>Creating a code…</span>}
          {error && <span role="alert" style={{ color: 'var(--color-error, #b91c1c)' }}>{error}</span>}
          {qr && (
            <>
              <img
                src={qr}
                alt="Hand-off QR code"
                width={240}
                height={240}
                style={{ opacity: expired ? 0.25 : 1, background: '#fff', borderRadius: 4 }}
              />
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: 'var(--color-text-secondary)' }}>
                <Smartphone size={14} />
                {expired
                  ? 'This code has expired — make a new one.'
                  : `Single use · expires in ${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`}
              </span>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
